import moment from 'moment-timezone';
import { localeMeta } from '@/languages/meta';

export const LOCALE_DICT = Object.fromEntries(
    localeMeta.languages.map(({ code, datepickerLocale }) => [code, datepickerLocale]),
);
export const HOUR_IN_SECONDS = 3600;
export const DAY_IN_SECONDS = 86400;
const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;

export function toSiteTime(date, siteUTCOffset) {
    // Offset between user local (browser) time and UTC
    const localUTCOffset = new Date(date).getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + localUTCOffset + siteUTCOffset);
}

export function toRangeTimestamp(siteTzTimestamp, siteUTCOffset, siteZone) {
    // Offset between user local (browser) time and UTC
    const localUTCOffset = new Date(siteTzTimestamp).getTimezoneOffset() * 60 * 1000;

    // Check if there's a difference between timestamp and now due to daylight savings
    const zone = moment.tz.zone(siteZone);
    const daylightSavingsDIff =
        (zone.parse(Date.now()) - zone.parse(new Date(siteTzTimestamp).getTime())) * 60 * 1000;
    return Math.floor(
        (siteTzTimestamp - localUTCOffset - siteUTCOffset - daylightSavingsDIff) / 1000,
    );
}

export function findRangeByName(rangeName, DESSRanges = false) {
    return (DESSRanges ? DESS_RANGES : DASHBOARD_RANGES)
        .flat()
        .find(range => range.name === rangeName);
}

export function findRangeByDates(
    startDate,
    endDate,
    siteUTCOffset,
    DESSRanges = false,
    startOfWeek,
) {
    const currentSiteTime = toSiteTime(new Date(), siteUTCOffset);
    return (DESSRanges ? DESS_RANGES : DASHBOARD_RANGES)
        .flat()
        .find(
            range =>
                Math.abs(range.startDate(currentSiteTime, startOfWeek) - startDate) <
                    FIVE_MINUTES_IN_MILLISECONDS &&
                Math.abs(range.endDate(currentSiteTime, startOfWeek) - endDate) <
                    FIVE_MINUTES_IN_MILLISECONDS,
        );
}

export function computePrecision(interval) {
    switch (interval) {
        case '15mins':
            return HOUR_IN_SECONDS / 4;
        case 'hours':
            return HOUR_IN_SECONDS;
        case 'days':
            return DAY_IN_SECONDS;
        case 'weeks':
            return 7 * DAY_IN_SECONDS;
        case 'months':
            return (DAY_IN_SECONDS * 365.25) / 12;
        case 'years':
            return DAY_IN_SECONDS * 365.25;
        default:
            return DAY_IN_SECONDS;
    }
}

export function roundTimestamp(date, intervalName, siteUTCOffset, siteTimezone) {
    const momentInterval = intervalName.slice(0, -1);
    // Round start and end time according to range.interval
    // Remove the terminal 's' from range.interval to obtain moment units
    switch (momentInterval) {
        case '15min': {
            // 15min is not a valid moment unit, thus apply rounding logic
            const input = toRangeTimestamp(date, siteUTCOffset, siteTimezone);
            const precision = computePrecision(intervalName);
            return Math.floor(input / precision) * precision;
        }
        default: {
            return toRangeTimestamp(
                moment(date).startOf(momentInterval),
                siteUTCOffset,
                siteTimezone,
            );
        }
    }
}

export function getFallbackTranslation(name) {
    let fallback = name.replaceAll('qr_', '').replaceAll('_', ' ');
    fallback = fallback.charAt(0).toUpperCase() + fallback.slice(1);
    return fallback;
}

export const DASHBOARD_RANGES = [
    [
        {
            name: 'qr_last_2_days',
            startDate: sN =>
                moment(sN)
                    .subtract(2, 'day')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 2,
            shiftUnit: 'day',
            endsNow: true,
        },
        {
            name: 'qr_last_7_days',
            startDate: sN =>
                moment(sN)
                    .subtract(7, 'day')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 7,
            shiftUnit: 'day',
            endsNow: true,
        },
        {
            name: 'qr_last_30_days',
            startDate: sN =>
                moment(sN)
                    .subtract(30, 'day')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 30,
            shiftUnit: 'day',
            endsNow: true,
        },
        {
            name: 'qr_last_90_days',
            startDate: sN =>
                moment(sN)
                    .subtract(90, 'day')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 90,
            shiftUnit: 'day',
            endsNow: true,
        },
        {
            name: 'qr_last_6_months',
            startDate: sN =>
                moment(sN)
                    .subtract(6, 'months')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 6,
            shiftUnit: 'month',
            endsNow: true,
        },
        {
            name: 'qr_last_12_months',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'year')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 1,
            shiftUnit: 'year',
            endsNow: true,
        },
    ],
    [
        {
            name: 'qr_yesterday',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'day')
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(1, 'day')
                    .endOf('day')
                    .add(1, 'ms')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_day_before',
            next: 'qr_today',
        },
        {
            name: 'qr_day_before',
            startDate: sN =>
                moment(sN)
                    .subtract(2, 'day')
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(2, 'day')
                    .endOf('day')
                    .add(1, 'ms')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            next: 'qr_yesterday',
        },
        {
            name: 'qr_this_day_last_week',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'week')
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(1, 'week')
                    .endOf('day')
                    .add(1, 'ms')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
        },
        {
            name: 'qr_previous_month',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'month')
                    .startOf('month')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(1, 'month')
                    .endOf('month')
                    .add(1, 'ms')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'month',
            next: 'qr_this_month',
        },
        {
            name: 'qr_previous_week',
            startDate: (sN, startOfWeek) =>
                moment(sN)
                    .subtract(1, 'week')
                    .startOf('week')
                    .day(startOfWeek)
                    .toDate(),
            endDate: (sN, startOfWeek) =>
                moment(sN)
                    .subtract(1, 'week')
                    .startOf('week')
                    .day(startOfWeek)
                    .add(6, 'days')
                    .endOf('day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'week',
            next: 'qr_this_week',
        },
    ],
    [
        {
            name: 'qr_today',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_yesterday',
            next: 'qr_tomorrow',
            keepRealtime: true,
        },
        {
            name: 'qr_this_week',
            startDate: (sN, startOfWeek) =>
                // By default, start of the week is sunday. 0-7 day indexes.
                // ISO 8601 starts the week on Monday. This ensures the correct start day is grabbed when
                // monday is the preferred start of the week the user has selected.
                moment(sN)
                    .startOf(startOfWeek === 'monday' ? 'isoWeek' : 'week')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 1,
            shiftUnit: 'week',
            prev: 'qr_previous_week',
            endsNow: true,
        },
        {
            name: 'qr_this_month',
            startDate: sN =>
                moment(sN)
                    .startOf('month')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 1,
            shiftUnit: 'month',
            prev: 'qr_previous_month',
            endsNow: true,
        },
        {
            name: 'qr_this_year',
            startDate: sN =>
                moment(sN)
                    .startOf('year')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 1,
            shiftUnit: 'year',
            prev: 'qr_last_year',
            endsNow: true,
        },
        {
            name: 'qr_last_year',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'year')
                    .startOf('year') // Start of the previous year (January 1)
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(1, 'year')
                    .endOf('year') // End of the previous year (December 31)
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'year',
            endsNow: false,
        },
    ],
    [
        {
            name: 'qr_last_hour',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'hour')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 1,
            shiftUnit: 'hour',
            endsNow: true,
        },
        {
            name: 'qr_last_3_hours',
            startDate: sN =>
                moment(sN)
                    .subtract(3, 'hour')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 3,
            shiftUnit: 'hour',
            endsNow: true,
        },
        {
            name: 'qr_last_6_hours',
            startDate: sN =>
                moment(sN)
                    .subtract(6, 'hour')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 6,
            shiftUnit: 'hour',
            endsNow: true,
        },
        {
            name: 'qr_last_12_hours',
            startDate: sN =>
                moment(sN)
                    .subtract(12, 'hour')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 12,
            shiftUnit: 'hour',
            endsNow: true,
        },
        {
            name: 'qr_last_24_hours',
            startDate: sN =>
                moment(sN)
                    .subtract(24, 'hour')
                    .toDate(),
            endDate: sN => sN,
            shiftValue: 24,
            shiftUnit: 'hour',
            endsNow: true,
        },
    ],
    [
        {
            name: 'qr_tomorrow',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .add(1, 'day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .add(1, 'day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_today',
            next: 'qr_day_after_tomorrow',
        },
        {
            name: 'qr_day_after_tomorrow',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .add(2, 'day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .add(2, 'day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_tomorrow',
        },
        {
            name: 'qr_next_2_days',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .add(1, 'day')
                    .toDate(),
            shiftValue: 2,
            shiftUnit: 'day',
            interval: 'hours',
            keepRealtime: true,
        },
        {
            name: 'qr_next_7_days',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .add(6, 'day')
                    .toDate(),
            shiftValue: 7,
            shiftUnit: 'day',
            prev: 'qr_next_2_days',
            keepRealtime: true,
        },
    ],
];

export const DESS_RANGES = [
    [
        {
            name: 'qr_yesterday',
            startDate: sN =>
                moment(sN)
                    .subtract(1, 'day')
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(1, 'day')
                    .endOf('day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_day_before',
            next: 'qr_today',
        },
        {
            name: 'qr_day_before',
            startDate: sN =>
                moment(sN)
                    .subtract(2, 'day')
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .subtract(2, 'day')
                    .endOf('day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            next: 'qr_yesterday',
        },
        {
            name: 'qr_today',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_yesterday',
            next: 'qr_tomorrow',
            keepRealtime: true,
        },
        {
            name: 'qr_tomorrow',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .add(1, 'day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .add(1, 'day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_today',
            next: 'qr_day_after_tomorrow',
        },
        {
            name: 'qr_day_after_tomorrow',
            startDate: sN =>
                moment(sN)
                    .startOf('day')
                    .add(2, 'day')
                    .toDate(),
            endDate: sN =>
                moment(sN)
                    .endOf('day')
                    .add(2, 'day')
                    .toDate(),
            shiftValue: 1,
            shiftUnit: 'day',
            prev: 'qr_tomorrow',
        },
    ],
];
